'use client';

import { useEffect } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useViewedLocationPage from '@alltrails/core/hooks/useViewedLocationPage';
import { get } from '@alltrails/core/utils/get';
import logError from '@alltrails/core/utils/logError';
import useIsSeoFromDocumentReferrer from '@alltrails/networking/hooks/useIsSeoFromDocumentReferrer';
import { getItem } from '@alltrails/session-storage';
import { pageViewLimit, PageViewLimit } from '@alltrails/trails';
import type { TrailId } from '@alltrails/types/TrailId';
import ScrollLimit from '@alltrails/user-auth/components/ScrollLimit/ScrollLimit';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setViewLimitReached } from '@/utils/trailPageSlice';

type ViewLimitHandlerProps = {
  pageReferer: string | null;
  trailId: TrailId;
  isScrollLimitActive?: boolean;
};

export const isSessionSeoOrViralityTraffic = (): boolean => {
  const sessionAttribution = getItem<{ referring_domain?: string; virality_param?: string }>('sessionAttribution');

  if (!sessionAttribution) {
    return false;
  }

  const hasSeoTraffic = sessionAttribution.referring_domain?.includes('google') ?? false;
  const hasViralityTraffic = sessionAttribution.virality_param !== undefined;

  return hasSeoTraffic || hasViralityTraffic;
};

const ViewLimitHandler = ({ pageReferer, trailId, isScrollLimitActive }: ViewLimitHandlerProps) => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const limitReached = useAppSelector(state => state.trailPage.viewLimitReached);
  const { hasViewedLocationPage } = useViewedLocationPage();
  const isSeoFromDocumentReferrer = useIsSeoFromDocumentReferrer();

  useEffect(() => {
    const setViewLimit = async () => {
      if (user) {
        return;
      }

      try {
        const { limitReached } = await get<{ limitReached: boolean }>(`/api/mugen/update-view-limit`, {
          headers: {
            ...(!!pageReferer ? { 'X-Page-Referer': pageReferer } : {})
            // header to always show reg wall when referred from an external site
            // { 'X-External-Wall': true }
          }
        });
        dispatch(setViewLimitReached(limitReached));
      } catch (e: any) {
        logError(e);
      }
    };

    setViewLimit();
  }, [user]);

  const enableScrollLimit = isScrollLimitActive || isSessionSeoOrViralityTraffic() || isSeoFromDocumentReferrer || hasViewedLocationPage;

  return enableScrollLimit ? (
    <ScrollLimit deeplinkData={{ pageType: 'trail', pageId: trailId }} />
  ) : (
    <PageViewLimit limit={pageViewLimit} limitReached={limitReached === 'uninitialized' ? false : limitReached} trailId={trailId} />
  );
};

export default ViewLimitHandler;
